import { useMemo, useState } from 'react'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { Head1, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import useQueryPagination from 'hooks/useQueryPagination'
import { useHasPermission } from 'hooks/useHasPermission'
import { getFoodKitchens, getFoodMealWindows } from 'features/food/foodSlice'
import { DashboardTab } from './tabs/DashboardTab'
import { ProfilesTab } from './tabs/ProfilesTab'
import { ConfigurationTab } from './tabs/ConfigurationTab'
import { HistoryTab } from './tabs/HistoryTab'

const FOOD_TABS = ['Dashboard', 'Exceptions', 'Meal Requests', 'Profiles', 'Configuration', 'History']

const BackendGap = ({ title, detail }: { title: string; detail: string }) => (
  <Container>
    <ContainerHeader>
      <TitleText weight='700'>{title}</TitleText>
    </ContainerHeader>
    <ContainerContent>
      <OtherText grey>{detail}</OtherText>
    </ContainerContent>
  </Container>
)

export const Food = () => {
  const canKitchen = useHasPermission('kitchen')
  const [activeTab, setActiveTab] = useState('Dashboard')
  const [configurationToolbar, setConfigurationToolbar] = useState<React.ReactNode>(null)

  const params = useMemo(() => ({ page: 1, limit: 200 }), [])
  const fetchFunctions = useMemo(
    () => (canKitchen ? [getFoodMealWindows, getFoodKitchens] : [getFoodMealWindows]),
    [canKitchen],
  )

  useQueryPagination({
    fetchFunctions,
    cleanUpFunctions: [],
    params,
  })

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    if (tab !== 'Configuration') setConfigurationToolbar(null)
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16'>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Food
          </Head1>
        </Flex>
        <Flex between alignEnd mb='16' width='100%' wrap gap='1rem'>
          <TabsContainer>
            {FOOD_TABS.map((tab) => (
              <Tabs key={tab} active={activeTab === tab} onClick={() => handleTabChange(tab)}>
                {tab}
              </Tabs>
            ))}
          </TabsContainer>
          {activeTab === 'Configuration' && configurationToolbar}
        </Flex>
        {activeTab === 'Dashboard' && <DashboardTab />}
        {activeTab === 'Exceptions' && (
          <BackendGap
            title='Exceptions'
            detail='No Food exception endpoints are available yet. This tab needs a backend list/resolve API.'
          />
        )}
        {activeTab === 'Meal Requests' && (
          <BackendGap
            title='Meal Requests'
            detail='No Food meal-request endpoints are available yet. This tab needs create/list/approve APIs.'
          />
        )}
        {activeTab === 'Profiles' && <ProfilesTab />}
        {activeTab === 'Configuration' && (
          <ConfigurationTab onToolbarChange={setConfigurationToolbar} />
        )}
        {activeTab === 'History' && <HistoryTab />}
      </PageWrapper>
    </>
  )
}
